<template>
  <b-card class="filter">
    <div>
      <div v-if="customers.length" class="pb-4"><b>Customer:</b> {{ customers.map((c) => c.name).join(', ') }}</div>

      <div class="d-flex pb-3 status-filter justify-content-between w-100">
        <div class="d-flex">
          <div v-for="{ value, text } in statusesData" :key="value" class="pr-4">
            <div class="form-check">
              <input
                :id="'status-' + value"
                v-model="statuses"
                class="form-check-input"
                type="checkbox"
                :value="value"
              />
              <label class="form-check-label" :for="'status-' + value">
                {{ text }}
              </label>
            </div>
          </div>
        </div>

        <div v-if="can('manage', 'staff.area')">
          <SimpleDropdown v-model="proxyMode" :options="proxyModeOptions" />
        </div>
      </div>

      <div class="d-flex pb-3">
        <div v-for="carrierHandle in carriersData" :key="carrierHandle" class="pr-4">
          <div class="form-check">
            <input
              :id="'carrier-' + carrierHandle"
              v-model="carriers"
              class="form-check-input"
              type="checkbox"
              :value="carrierHandle"
            />
            <label class="form-check-label" :for="'carrier-' + carrierHandle">
              <CarrierRowWithIcon :carrier="carrierHandle" />
            </label>
          </div>
        </div>
      </div>

      <div class="d-flex pb-3 delivery-filter">
        <div class="pr-4">
          <div class="d-flex">
            <div class="form-check mr-3">
              <input id="diverted" v-model="packagesDivertedToServicePoint" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="diverted"> Diverted to service-point </label>
            </div>
            <div v-if="packagesDivertedToServicePoint" class="form-check">
              <input
                id="service-point"
                v-model="withServicePointCollectingProblem"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="service-point"> Where we could not collect service-point yet </label>
            </div>
          </div>
        </div>
        <div class="pr-4">
          <div class="form-check">
            <input
              id="recipient-problem"
              v-model="withRecipientNotificationProblem"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label" for="recipient-problem"> With problems notifying recipient </label>
          </div>
        </div>
        <div v-if="can('manage', 'staff.area')" class="pr-4">
          <div class="form-check">
            <input id="checkedIn" v-model="checkedIn" class="form-check-input" type="checkbox" />
            <label class="form-check-label" for="checkedIn"> Checked-in </label>
          </div>
        </div>
        <div class="pr-4">
          <div class="form-check">
            <input id="returnAssignments" v-model="returnAssignments" class="form-check-input" type="checkbox" />
            <label class="form-check-label" for="returnAssignments"> Return assignments </label>
          </div>
        </div>
      </div>

      <div class="d-flex pb-3 distribution-channel-filter">
        <div v-for="{ value, text } in distributionChannelsData" :key="value" class="pr-4">
          <div class="form-check">
            <input
              :id="'distribution-channel-' + value"
              v-model="distributionChannels"
              class="form-check-input"
              type="checkbox"
              :value="value"
            />
            <label class="form-check-label" :for="'distribution-channel-' + value">
              {{ text }}
            </label>
          </div>
        </div>
      </div>

      <div class="d-flex gap-2 flex-wrap align-items-start shipments-filter-inputs">
        <div class="filter-field text-input-block">
          <input v-model="search" class="form-control" type="text" placeholder="Shipment no / recipient" />
        </div>
        <div v-if="can('manage', 'staff.area')" class="filter-field">
          <CompanySearchSelect v-model="selectedCompanyId" placeholder="Company..." @update:model-value="submit" />
        </div>
        <div class="filter-field date-block">
          <vue-date-picker
            v-model="shippedAt"
            format="yyyy-MM-dd"
            range
            multi-calendars
            placeholder="Created at"
            :clearable="true"
          >
          </vue-date-picker>
        </div>
        <div class="filter-field text-input-block">
          <input v-model="country" class="form-control" type="text" placeholder="Country code" />
        </div>
        <div class="filter-field text-input-block">
          <input v-model="email" class="form-control" type="text" placeholder="Email" />
        </div>
        <div class="filter-field text-input-block">
          <input v-model="phone" class="form-control" type="text" placeholder="Phone" />
        </div>
        <div class="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
          <b-button v-if="hasSelectedFilters" class="reset-button" variant="secondary" @click="reset">
            Reset
          </b-button>
          <b-button variant="success" class="submit-button" @click="submit"> Submit </b-button>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script lang="ts">
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { User } from '@/types/Models/User'
import type { ComputedRef, Ref } from 'vue'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { BButton, BCard } from 'bootstrap-vue-next'
import { ref, computed, watch, onMounted, defineComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { CUSTOMER } from '@/const/AccountTypes'
import { CARRIERS } from '@/const/Carriers'
import { BOX, LETTER, PACKAGE, PARCEL } from '@/const/DistributionChannels'
import { none } from '@/services/Helpers'
import { useAccountStore } from '@/store/account'
import { ProxyMode, type ShipmentsQueryParams } from '@/types/Components/SearchForm'
import { CUSTOMER_SHIPMENTS, STAFF_SHIPMENTS } from '@/types/router'
import moment from 'moment'
import CompanyProxy from '@/proxies/CompanyProxy'
import SimpleDropdown from '@/views/Components/PageParts/SimpleDropdown.vue'
import CarrierRowWithIcon from '@/views/Components/Assignment/CarrierRowWithIcon.vue'
import CompanySearchSelect from '@/views/Components/Elements/Form/CompanySearchSelect.vue'

export default defineComponent({
  name: 'ShipmentsSearchForm',
  components: { BCard, BButton, SimpleDropdown, VueDatePicker, CarrierRowWithIcon, CompanySearchSelect },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const accountStore = useAccountStore()

    const customers: Ref<User[]> = ref([])
    const customerIds: Ref<string[]> = ref([])
    const selectedCompanyId: Ref<string | null> = ref(null)
    const statuses: Ref<string[]> = ref([])
    const carriers: Ref<string[]> = ref([])
    const distributionChannels: Ref<string[]> = ref([])
    const search: Ref<string | null> = ref(null)
    const country: Ref<string | null> = ref(null)
    const email: Ref<string | null> = ref(null)
    const phone: Ref<string | null> = ref(null)
    const shippedAt: Ref<string[] | null> = ref(null)
    const checkedIn: Ref<boolean> = ref(false)
    const returnAssignments: Ref<boolean> = ref(false)
    const packagesDivertedToServicePoint: Ref<boolean> = ref(false)
    const withServicePointCollectingProblem: Ref<boolean> = ref(false)
    const withRecipientNotificationProblem: Ref<boolean> = ref(false)
    const proxyMode: Ref<string | undefined> = ref(undefined)
    const carriersData = CARRIERS
    const distributionChannelsData: SelectOptions[] = [
      { value: PACKAGE, text: 'Packet' },
      { value: PARCEL, text: 'Parcel' },
      { value: BOX, text: 'Parcel locker' },
      { value: LETTER, text: 'Letter' },
    ]

    const statusesData: SelectOptions[] = [
      { value: 'out_for_delivery', text: 'Out for delivery' },
      { value: 'delivered', text: 'Delivered' },
      { value: 'delivery_issue', text: 'Delivery issue' },
      { value: 'returned', text: 'Returned' },
    ]

    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const ability = computed(() => accountStore.ability)
    const can = (rule: string, section: string) => ability.value.can(rule as any, section as any)
    const routeHandle: ComputedRef<string> = computed(() => (isStaff.value ? STAFF_SHIPMENTS : CUSTOMER_SHIPMENTS))
    const selectedFiltersCount: ComputedRef<number> = computed(() => {
      let count = 0

      if (search.value) count++
      if (email.value) count++
      if (phone.value) count++
      if (country.value) count++
      if (checkedIn.value) count++
      if (returnAssignments.value) count++
      if (shippedAt.value) count++
      if (statuses.value.length) count++
      if (carriers.value.length) count++
      if (distributionChannels.value.length) count++
      if (customerIds.value.length) count++
      if (selectedCompanyId.value) count++
      if (packagesDivertedToServicePoint.value) count++
      if (withServicePointCollectingProblem.value) count++
      if (withRecipientNotificationProblem.value) count++
      if (proxyMode.value) count++

      return count
    })

    const hasSelectedFilters: ComputedRef<boolean> = computed(() => Boolean(selectedFiltersCount.value))

    const proxyModeOptions = computed(() => [
      { value: null, text: 'With and without proxy' },
      { value: ProxyMode.WITH, text: 'With proxy' },
      { value: ProxyMode.WITHOUT, text: 'Without proxy' },
    ])

    const submit = () => {
      const query = {} as ShipmentsQueryParams

      query.checkedIn = checkedIn.value ? 'true' : undefined
      query.returnAssignments = returnAssignments.value ? 'true' : undefined
      query.packagesDivertedToServicePoint = packagesDivertedToServicePoint.value ? 'true' : undefined
      query.withRecipientNotificationProblem = withRecipientNotificationProblem.value ? 'true' : undefined
      query.withServicePointCollectingProblem = withServicePointCollectingProblem.value ? 'true' : undefined
      query.statuses = statuses.value.length ? statuses.value.map(String).join(',') : undefined
      query.carriers = carriers.value.length ? carriers.value.map(String).join(',') : undefined
      query.distributionChannel = distributionChannels.value.length
        ? distributionChannels.value.map(String).join(',')
        : undefined
      query.country = country.value ? String(country.value).trim() : undefined
      query.email = email.value ? String(email.value).trim() : undefined
      query.phone = phone.value ? String(phone.value).trim() : undefined
      query.query = search.value ? String(search.value).trim() : undefined
      query.proxyMode = proxyMode.value ? proxyMode.value : undefined
      query.customer = selectedCompanyId.value ? selectedCompanyId.value : undefined

      if (Array.isArray(shippedAt.value) && shippedAt.value.length === 2) {
        const [dateFrom, dateTo] = shippedAt.value
        query.dateFrom = moment(dateFrom).format('YYYY-MM-DD')
        query.dateTo = moment(dateTo).format('YYYY-MM-DD')
      }

      router.push({ name: routeHandle.value, query })
    }

    const reset = () => {
      router.push({ name: routeHandle.value })
    }

    watch(customerIds, (value) => {
      if (!value.length) {
        return
      }

      const parameters = {
        type: CUSTOMER,
        [`id[]`]: Array.isArray(value) ? value : [value],
      }

      new CompanyProxy(parameters)
        .all()
        .then((response) => (customers.value = response.data))
        .catch(none)
    })

    watch(packagesDivertedToServicePoint, (value) => {
      if (!value) {
        withServicePointCollectingProblem.value = false
      }
    })

    watch(shippedAt, submit)

    onMounted(() => {
      const routeQuery = route.query
      checkedIn.value = Boolean(routeQuery.checkedIn || false)
      returnAssignments.value = Boolean(routeQuery.returnAssignments || false)
      packagesDivertedToServicePoint.value = Boolean(routeQuery.packagesDivertedToServicePoint || false)
      withRecipientNotificationProblem.value = Boolean(routeQuery.withRecipientNotificationProblem || false)
      withServicePointCollectingProblem.value = Boolean(routeQuery.withServicePointCollectingProblem || false)
      customerIds.value = routeQuery.customer ? String(routeQuery.customer).split(',') : []
      selectedCompanyId.value = routeQuery.customer ? String(routeQuery.customer) : null
      statuses.value = routeQuery.statuses ? String(routeQuery.statuses).split(',') : []
      carriers.value = routeQuery.carriers ? String(routeQuery.carriers).split(',') : []
      distributionChannels.value = routeQuery.distributionChannel ? String(routeQuery.distributionChannel).split(',') : []
      country.value = routeQuery.country ? String(routeQuery.country) : null
      email.value = routeQuery.email ? String(routeQuery.email) : null
      phone.value = routeQuery.phone ? String(routeQuery.phone) : null
      search.value = routeQuery.query ? String(routeQuery.query) : null
      proxyMode.value = routeQuery.proxyMode ? String(routeQuery.proxyMode) : undefined

      if (routeQuery.shipmentNumber) {
        search.value = String(routeQuery.shipmentNumber)
      }
      if (routeQuery.dateFrom && routeQuery.dateTo) {
        shippedAt.value = [String(routeQuery.dateFrom), String(routeQuery.dateTo)]
      }
    })

    return {
      search,
      isStaff,
      can,
      country,
      email,
      phone,
      selectedCompanyId,
      statuses,
      carriers,
      distributionChannels,
      customers,
      shippedAt,
      checkedIn,
      returnAssignments,
      routeHandle,
      carriersData,
      distributionChannelsData,
      statusesData,
      hasSelectedFilters,
      packagesDivertedToServicePoint,
      withRecipientNotificationProblem,
      withServicePointCollectingProblem,
      submit,
      reset,
      proxyMode,
      proxyModeOptions,
    }
  },
})
</script>
